/*
 * Audio device (speaker/headset/mic) selection for the softphone.
 * Ring sounds and call audio can go to different devices, e.g. ring on the
 * speakers and talk on the headset. The call mic can be picked too.
 *
 * This is local browser config, not API config, so it lives here instead of the
 * store. The agent's choice is saved in localStorage per user. Anything not
 * picked uses the system default.
 *
 * State is module level so every component shares the same selection.
 */
import { computed, reactive } from 'vue';
import {
  DEFAULT_SINK,
  isSinkSelectionSupported,
} from 'src/util/audioDevices';

const STORAGE_PREFIX = 's2-audio-devices-';
// Chromium pseudo devices that alias a real device
const PSEUDO_DEVICES = [DEFAULT_SINK, 'communications'];

let deviceChangeListener = undefined;

const state = reactive({
  userId: undefined,
  outputDevices: [], // audiooutput MediaDeviceInfo, {deviceId, label, groupId}
  inputDevices: [], // audioinput MediaDeviceInfo, {deviceId, label, groupId}
  ringSinkIds: undefined, // undefined = not set by the agent
  callSinkId: undefined, // undefined = not set by the agent
  micDeviceId: undefined, // undefined = not set by the agent
});

// Drop devices that are no longer plugged in. Until we can see the device list
// (no ids before mic permission) keep the ids as is.
const resolveIds = (ids, devices) => {
  const known = devices.filter((d) => d.deviceId);
  let resolved = ids ?? [];
  if (known.length) {
    resolved = resolved.filter(
      (id) => id == DEFAULT_SINK || known.some((d) => d.deviceId == id)
    );
  }
  return resolved.length ? [...new Set(resolved)] : [DEFAULT_SINK];
};

const outputDevices = computed(() => state.outputDevices);

const ringSinkIds = computed(() =>
  resolveIds(state.ringSinkIds, state.outputDevices)
);

const callSinkId = computed(
  () =>
    resolveIds(
      state.callSinkId ? [state.callSinkId] : [],
      state.outputDevices
    )[0]
);

// Real microphones only. Chromium's 'default'/'communications' aliases can point
// at a device that captures nothing, and on Linux "Monitor of ..." inputs record
// the speakers rather than a mic.
const micDevices = computed(() =>
  state.inputDevices.filter(
    (d) =>
      d.deviceId &&
      !PSEUDO_DEVICES.includes(d.deviceId) &&
      !/^monitor of /i.test(d.label)
  )
);

// Always a real mic id once devices are listed, never the 'default' alias
const micDeviceId = computed(() => {
  const mics = micDevices.value;
  if (!mics.length) {
    // Can't see the devices yet (no mic permission), let the browser pick
    return state.micDeviceId ?? DEFAULT_SINK;
  }
  if (mics.some((d) => d.deviceId == state.micDeviceId)) {
    return state.micDeviceId;
  }
  // Not picked or unplugged: use the mic the system default points at, else the first
  const alias = state.inputDevices.find((d) => d.deviceId == DEFAULT_SINK);
  return (mics.find((d) => alias && d.groupId == alias.groupId) ?? mics[0])
    .deviceId;
});

// True when the agent picked their own devices rather than the defaults
const hasCustomDevices = computed(
  () =>
    state.ringSinkIds !== undefined ||
    state.callSinkId !== undefined ||
    state.micDeviceId !== undefined
);

const persist = () => {
  if (state.userId == undefined) {
    return;
  }
  try {
    const key = STORAGE_PREFIX + state.userId;
    if (!hasCustomDevices.value) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(
      key,
      JSON.stringify({
        ringSinkIds: state.ringSinkIds,
        callSinkId: state.callSinkId,
        micDeviceId: state.micDeviceId,
      })
    );
  } catch (e) {
    console.warn('Unable to save audio device settings', e);
  }
};

// Device names are hidden until we have mic permission. Softphone users already
// granted it, but the settings dialog can ask for it if needed.
const refreshAudioDevices = async (requestPermission = false) => {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return;
  }
  try {
    let devices = await navigator.mediaDevices.enumerateDevices();
    if (requestPermission && !devices.some((d) => d.label)) {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      stream.getTracks().forEach((t) => t.stop());
      devices = await navigator.mediaDevices.enumerateDevices();
    }
    const ofKind = (kind) =>
      devices
        .filter((d) => d.kind == kind)
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label,
          groupId: d.groupId,
        }));
    state.outputDevices = ofKind('audiooutput');
    state.inputDevices = ofKind('audioinput');
  } catch (e) {
    console.warn('Unable to list audio devices', e);
  }
};

// Load the saved choice for this user and start watching for device changes
const initAudioDevices = async (userId) => {
  let saved = {};
  try {
    saved = JSON.parse(localStorage.getItem(STORAGE_PREFIX + userId)) ?? {};
  } catch (e) {
    console.warn('Invalid audio device settings in localStorage', e);
  }
  state.userId = userId;
  state.ringSinkIds = saved.ringSinkIds;
  state.callSinkId = saved.callSinkId;
  state.micDeviceId = saved.micDeviceId;

  if (!deviceChangeListener && navigator.mediaDevices) {
    deviceChangeListener = () => refreshAudioDevices();
    navigator.mediaDevices.addEventListener('devicechange', deviceChangeListener);
  }
  return refreshAudioDevices();
};

const setRingSinkIds = (ids) => {
  state.ringSinkIds = ids?.length ? ids : [DEFAULT_SINK];
  persist();
};

const setCallSinkId = (id) => {
  state.callSinkId = id || DEFAULT_SINK;
  persist();
};

const setMicDeviceId = (id) => {
  state.micDeviceId = id || DEFAULT_SINK;
  persist();
};

// Go back to the system defaults
const resetAudioDevices = () => {
  state.ringSinkIds = undefined;
  state.callSinkId = undefined;
  state.micDeviceId = undefined;
  persist();
};

export const useAudioDevices = () => ({
  isOutputSelectionSupported: isSinkSelectionSupported(),
  outputDevices,
  micDevices,
  ringSinkIds,
  callSinkId,
  micDeviceId,
  hasCustomDevices,
  initAudioDevices,
  refreshAudioDevices,
  setRingSinkIds,
  setCallSinkId,
  setMicDeviceId,
  resetAudioDevices,
});
